import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LocationIcon } from '@hero/web-icons';
import { extractGoogleMapsUrl } from '@hero/agent';
import { useAddressApi, useSavedLocationResolveGoogleUrlApi } from '@hero/api-hooks';
import { ChoiceCard } from '../../ChoiceCard';
import { ModalForm } from '../../Modal';
import { Input } from '../../../ui/Input/Input';
import { MapLocationData } from '../types';

/** The accuracy answer, plus a better pin if the user supplied a link. */
export interface LocationAccuracyDecision {
  isAccurate: boolean;
  /**
   * Fields to merge into the location. Present only when a pasted Maps link
   * resolved — carries the new coordinates, the re-geocoded address, and the
   * URL itself.
   */
  patch?: Partial<MapLocationData>;
}

interface LocationAccuracyModalProps {
  location: MapLocationData | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (decision: LocationAccuracyDecision) => void;
}

type UrlState =
  | { status: 'idle' }
  | { status: 'resolving' }
  | { status: 'resolved'; patch: Partial<MapLocationData> }
  | { status: 'error'; reason: 'invalid' | 'unresolvable' };

/**
 * Ask whether the pin the user just placed is the exact spot.
 *
 * The answer is load-bearing rather than cosmetic: `isAccurateLocation`
 * decides whether WhatsApp sends the driver a real navigation link or the
 * "exact location will be shared soon" placeholder, and whether dispatch is
 * warned the driver may need to call on arrival.
 *
 * Two choice cards with a Cancel / Continue footer, matching
 * `LocationChangePriceModal`. The previous version had two full-width
 * buttons that each committed on click, so there was no way to read the
 * second option's consequence without having already accepted the first.
 *
 * A Google Maps link is offered inside the "no" card, because it is the
 * *remedy* for not having the exact spot rather than a note for dispatch to
 * open later: we resolve it, move the pin, re-geocode so the structured
 * address (and therefore the city, which drives lane pricing) matches where
 * the pin now is, and mark the stop exact. Same treatment
 * `useSavedLocationForm` gives a pasted link. The URL is kept as
 * provenance and rides along to `OrderDestination.locationUrl`.
 *
 * A link that won't resolve shows an inline error but never blocks Continue
 * — trapping someone behind a bad URL right after they said they don't have
 * the exact spot would be worse than saving an approximate pin.
 */
export function LocationAccuracyModal({
  location,
  isOpen,
  onClose,
  onConfirm,
}: LocationAccuracyModalProps) {
  const { t } = useTranslation(['m-location', 'c-main']);

  const [isAccurate, setIsAccurate] = useState<boolean | null>(null);
  const [url, setUrl] = useState('');
  const [urlState, setUrlState] = useState<UrlState>({ status: 'idle' });
  /** In-flight link lookup, so a commit can wait for it. */
  const pendingResolve = useRef<Promise<UrlState> | null>(null);

  const resolveGoogleUrl = useSavedLocationResolveGoogleUrlApi();
  const addressApi = useAddressApi();

  useEffect(() => {
    if (!isOpen) return;
    setIsAccurate(null);
    setUrl(location?.locationUrl ?? '');
    setUrlState({ status: 'idle' });
    pendingResolve.current = null;
  }, [isOpen, location?.locationUrl]);

  const fullAddress = location?.fullAddress || location?.address?.fullAddress || '';

  /**
   * Turn the pasted link into a pin. Runs on blur rather than per-keystroke
   * so a half-pasted URL doesn't produce a stream of failed lookups.
   *
   * Returns the outcome and parks the promise on a ref, because blurring the
   * field is usually the same gesture as clicking Continue — the commit has
   * to be able to wait for the lookup instead of saving an approximate pin
   * the user believes they just fixed.
   */
  const resolveUrl = async (): Promise<UrlState> => {
    const raw = url.trim();
    const settle = (next: UrlState) => {
      setUrlState(next);
      return next;
    };

    if (!raw) return settle({ status: 'idle' });

    const mapsUrl = extractGoogleMapsUrl(raw);
    if (!mapsUrl) return settle({ status: 'error', reason: 'invalid' });

    setUrlState({ status: 'resolving' });
    const pending = (async (): Promise<UrlState> => {
      try {
        const point = await resolveGoogleUrl.mutateAsync({ url: mapsUrl });
        const cords = { lat: point.lat, lng: point.lng };
        // Re-geocode rather than trusting the link's own label: `address.city`
        // decides the lane, and a stop whose pin moved to Dammam while its
        // city still says Jeddah prices the wrong route.
        const address = await addressApi.mutateAsync(cords).catch(() => undefined);
        return settle({
          status: 'resolved',
          patch: {
            cords,
            locationUrl: mapsUrl,
            ...(address
              ? { address, fullAddress: address.fullAddress, city: address.city?.name }
              : { fullAddress: point.address ?? point.name }),
          },
        });
      } catch {
        return settle({ status: 'error', reason: 'unresolvable' });
      }
    })();

    pendingResolve.current = pending;
    return pending;
  };

  /**
   * Picking "yes" means the pin currently on the map, so a link resolved a
   * moment ago is discarded rather than silently moving the pin the user
   * just vouched for. Any URL the stop arrived with is untouched — that
   * lives on the location, not in this state.
   */
  const selectAccurate = () => {
    setIsAccurate(true);
    setUrl(location?.locationUrl ?? '');
    setUrlState({ status: 'idle' });
    pendingResolve.current = null;
  };

  const handleContinue = async () => {
    const settled = pendingResolve.current ? await pendingResolve.current : urlState;
    // A resolved link *is* the exact spot, whichever card is selected.
    if (settled.status === 'resolved') {
      return onConfirm({ isAccurate: true, patch: settled.patch });
    }
    onConfirm({ isAccurate: isAccurate === true });
  };

  const isResolving = urlState.status === 'resolving';

  return (
    <ModalForm
      isOpen={isOpen}
      onClose={onClose}
      title={t('modal.accuracy_title', 'Is this location accurate?')}
      description={t(
        'modal.accuracy_description',
        'Tell dispatch whether the driver can navigate straight to this pin.',
      )}
      onSave={handleContinue}
      saveText={t('modal.accuracy_continue', 'Continue')}
      disableSave={isAccurate === null || isResolving}
      isLoading={isResolving}
      // Opens over the map dialog, and the answer is load-bearing — an
      // outside click here would land on the map and drop the question.
      allowOutsideClickClose={false}
      size="md">
      <div className="space-y-4 text-sm">
        {fullAddress ? (
          <div className="flex items-start gap-3 rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2.5">
            <LocationIcon className="mt-0.5 size-5 shrink-0 text-primary" />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-slate-500">
                {t('modal.accuracy_selected_location', 'Selected location')}
              </p>
              <p className="break-words text-sm text-slate-700">{fullAddress}</p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-600">
            {t('modal.accuracy_no_location', 'No location selected.')}
          </p>
        )}

        <div className="space-y-2">
          <ChoiceCard
            selected={isAccurate === true}
            onSelect={selectAccurate}
            disabled={isResolving}
            label={t('modal.accuracy_yes_title', 'Yes, this pin is exact')}
            hint={t('modal.accuracy_yes_hint', 'The driver navigates straight to it.')}
          />

          <ChoiceCard
            selected={isAccurate === false}
            onSelect={() => setIsAccurate(false)}
            disabled={isResolving}
            label={t('modal.accuracy_no_title', "No — I don't have the exact spot yet")}
            // Dropped once a link resolves, because at that point the pin is
            // exact and nobody is being warned about anything.
            hint={
              urlState.status === 'resolved'
                ? undefined
                : t(
                    'modal.accuracy_no_hint',
                    'Dispatch is warned the driver may need to call on arrival.',
                  )
            }>
            {isAccurate === false ? (
              <div className="space-y-1.5 pt-0.5">
                <p className="text-xs text-slate-500">
                  {t(
                    'modal.accuracy_url_hint',
                    "Have a Google Maps link? Paste it and we'll place the pin exactly.",
                  )}
                </p>
                <Input
                  value={url}
                  onChange={e => setUrl(e.target.value)}
                  onBlur={resolveUrl}
                  onClick={e => e.stopPropagation()}
                  disabled={isResolving}
                  placeholder={t('form.location_url.placeholder')}
                />
                {urlState.status === 'resolving' ? (
                  <p className="text-xs text-slate-500">{t('form.location_url.resolving')}</p>
                ) : null}
                {urlState.status === 'resolved' ? (
                  <p className="text-xs font-medium text-emerald-700">
                    {t(
                      'modal.accuracy_url_resolved',
                      "Pin moved to the link's location — marked exact.",
                    )}
                  </p>
                ) : null}
                {urlState.status === 'error' ? (
                  <p className="text-xs text-red-600">
                    {urlState.reason === 'invalid'
                      ? t('form.location_url.error_invalid')
                      : t('form.location_url.error_unresolvable')}
                  </p>
                ) : null}
              </div>
            ) : null}
          </ChoiceCard>
        </div>
      </div>
    </ModalForm>
  );
}
